/** Parse YYYY-MM-DD as a local calendar date (avoids UTC off-by-one). */
export function parseYMD(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  if (y == null || m == null || d == null) return new Date(NaN);
  return new Date(y, m - 1, d);
}

export function formatYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function addDaysYmd(ymd: string, days: number): string {
  const date = parseYMD(ymd);
  date.setDate(date.getDate() + days);
  return formatYMD(date);
}

/** Inclusive number of calendar days in [fromStr, toStr]. */
export function inclusiveDayCount(fromStr: string, toStr: string): number | null {
  if (!fromStr || !toStr) return null;
  const a = parseYMD(fromStr);
  const b = parseYMD(toStr);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const ms =
    Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) -
    Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  return Math.floor(ms / 86400000) + 1;
}

/** Bridge API strings to DayPicker range selection. */
export function stringsToDateRange(
  dateFrom: string,
  dateTo: string
): { from: Date | undefined; to?: Date | undefined } | undefined {
  if (!dateFrom && !dateTo) return undefined;
  const from = dateFrom ? parseYMD(dateFrom) : undefined;
  const to = dateTo ? parseYMD(dateTo) : undefined;
  if (from && Number.isNaN(from.getTime())) return undefined;
  if (to && Number.isNaN(to.getTime())) return undefined;
  if (from && !to) return { from, to: undefined };
  if (from && to) return { from, to };
  return undefined;
}

/** Display date as DD.MM.YYYY. Prefer for API YYYY-MM-DD; falls back for ISO datetimes or Date. */
export function formatDateEuropean(dateInput: string | Date | null | undefined): string {
  if (dateInput == null || dateInput === '') return '';
  if (dateInput instanceof Date) {
    if (Number.isNaN(dateInput.getTime())) return '';
    const day = String(dateInput.getDate()).padStart(2, '0');
    const month = String(dateInput.getMonth() + 1).padStart(2, '0');
    const year = dateInput.getFullYear();
    return `${day}.${month}.${year}`;
  }
  const trimmed = dateInput.trim();
  const ymd =
    trimmed.length >= 10 && trimmed[4] === '-' && trimmed[7] === '-'
      ? trimmed.slice(0, 10)
      : null;
  const d = ymd ? parseYMD(ymd) : new Date(trimmed);
  if (Number.isNaN(d.getTime())) return dateInput;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

/** Human-readable local date/time (e.g. Telegram linked timestamp). */
export function formatDateDisplay(value: string | Date, includeTime?: boolean): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return String(value);
  return includeTime
    ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}
